/**
 * F20 (Wave 2D) — admin payment-disputes API client.
 *
 * Endpoints:
 *   GET  /admin/payment-disputes               → listQueue (paginated, status filter)
 *   GET  /admin/payment-disputes/:id           → detail
 *   POST /admin/payment-disputes/:id/resolve   → resolve (super_admin + support only)
 *
 * Permission gating happens server-side. Finance role sees the list + detail
 * read-only; only super_admin + support can resolve (Decision 7).
 *
 * The list response envelope follows the venue-claim queue pattern:
 *   `{ items: DisputeQueueRow[], nextCursor: string | null, total?: number }`.
 *
 * Cursor pagination, default sort = `created_at ASC` within status (oldest open
 * disputes surface first — matches Decision 7).
 */
import type {
  AdminPageMeta,
  BookingDetail,
  PaymentDispute,
  PaymentDisputeStatus,
  ResolveDisputeInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

// ─── Response envelopes ─────────────────────────────────────────────────────

export interface DisputeBookingRef {
  readonly id: string;
  readonly venueId: string;
  readonly venueName: string | null;
  readonly subVenueName: string | null;
  readonly sportName: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly priceAmount: number;
  readonly priceCurrency: string;
  readonly hostDisplayName: string | null;
}

export interface DisputeFilerRef {
  readonly id: string;
  readonly displayName: string | null;
  readonly email: string | null;
}

export interface PaymentDisputeQueueRow extends PaymentDispute {
  readonly booking: DisputeBookingRef | null;
  readonly filer: DisputeFilerRef | null;
}

export interface PaymentDisputeQueueResponse {
  readonly items: readonly PaymentDisputeQueueRow[];
  readonly nextCursor: string | null;
  readonly total?: number;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

export interface PaymentDisputeDetailResponse {
  readonly dispute: PaymentDispute;
  readonly booking: BookingDetail | null;
  readonly filer: DisputeFilerRef | null;
  /** Resolved/withdrawn dispute history rows for the same booking. */
  readonly bookingDisputeHistory: readonly PaymentDispute[];
}

// ─── List options ───────────────────────────────────────────────────────────

export interface PaymentDisputeQueueOpts {
  /** Server enum: `open | resolved_customer_correct | resolved_venue_correct | withdrawn`. */
  readonly status?: PaymentDisputeStatus | 'all';
  readonly venueId?: string;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
  /** When true the server returns only `{ total }` (count-mode poll). */
  readonly count?: boolean;
}

interface DataEnvelope<T> {
  readonly data: T;
}

function buildListQuery(opts: PaymentDisputeQueueOpts): string {
  const params: Record<string, string | number | boolean | undefined | null> = {
    limit: opts.limit,
    cursor: opts.cursor,
    venueId: opts.venueId,
    page: opts.page,
    perPage: opts.perPage,
    count: opts.count,
  };
  if (opts.status && opts.status !== 'all') {
    params.status = opts.status;
  }
  return buildQuery(params);
}

// ─── API surface ────────────────────────────────────────────────────────────

export const adminPaymentDisputesApi = {
  /**
   * GET /admin/payment-disputes — paginated queue.
   *
   * Default sort = `created_at ASC` within status (oldest open first; matches
   * Decision 7). The server enforces the sort; the client only forwards filters.
   */
  listQueue: async (opts?: PaymentDisputeQueueOpts): Promise<PaymentDisputeQueueResponse> => {
    // The API returns `{ data: PaymentDisputeQueueRow[], nextCursor, total }`
    // (the `data` field is the items array, not a wrapper object). Map to
    // the consumer-side shape `{ items, nextCursor, total }`.
    const raw = await adminFetch<{
      data: readonly PaymentDisputeQueueRow[];
      nextCursor: string | null;
      total?: number;
      pagination?: AdminPageMeta;
    }>(`/admin/payment-disputes${buildListQuery(opts ?? {})}`);
    return {
      items: raw.data,
      nextCursor: raw.nextCursor,
      ...(raw.total !== undefined ? { total: raw.total } : {}),
      ...(raw.pagination !== undefined ? { pagination: raw.pagination } : {}),
    };
  },

  /** GET /admin/payment-disputes/:id — full detail. */
  detail: async (id: string): Promise<PaymentDisputeDetailResponse | null> => {
    try {
      // API wraps detail as `{ data: { dispute, booking, filer,
      // bookingDisputeHistory } }`. `bookingDisputeHistory` is defensively
      // defaulted to [] so the page can't crash on `.length` access.
      const wrapped = await adminFetch<DataEnvelope<Partial<PaymentDisputeDetailResponse>>>(
        `/admin/payment-disputes/${encodeURIComponent(id)}`,
      );
      const d = wrapped.data;
      return {
        ...d,
        bookingDisputeHistory: d.bookingDisputeHistory ?? [],
      } as PaymentDisputeDetailResponse;
    } catch (err) {
      if (
        err !== null &&
        typeof err === 'object' &&
        'status' in err &&
        (err as { status: number }).status === 404
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * POST /admin/payment-disputes/:id/resolve — super_admin + support only.
   *
   * Outcome `customer_correct` triggers F17 force-transition + F23 commission
   * reversal hook server-side; the client only forwards the outcome + notes.
   *
   * The wire shape is `{ data: PaymentDispute }` — the BARE updated dispute
   * row, NOT the detail envelope. Callers must merge it into any detail state
   * they hold (`setData((prev) => prev ? { ...prev, dispute } : prev)`) —
   * replacing the whole detail with this value crashes the page (BP-01).
   */
  resolve: async (id: string, input: ResolveDisputeInput): Promise<PaymentDispute> => {
    const body = await adminFetch<DataEnvelope<PaymentDispute>>(
      `/admin/payment-disputes/${encodeURIComponent(id)}/resolve`,
      {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },
};
